/**
 * WebSocket Hook - Real-time connection to backend
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import { authStore } from '../stores/AuthStore';

export interface WebSocketMessage {
  type: string;
  [key: string]: any;
}

export interface ExecutionUpdate {
  type: string;
  execution_id: string;
  agent_id?: string;
  agent_name?: string;
  status?: string;
  step?: string;
  progress?: number;
  output?: any;
  duration_ms?: number;
  token_usage?: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
  cost?: number;
  error?: string;
  timestamp: string;
}

export interface TeamExecutionUpdate {
  type: string;
  execution_id: string;
  team_id?: string;
  team_name?: string;
  strategy?: string;
  status?: string;
  agent_id?: string;
  agent_name?: string;
  role?: string;
  output?: any;
  duration_ms?: number;
  total_tokens?: number;
  total_cost?: number;
  error?: string;
  progress_message?: string;
  current_iteration?: number;
  timestamp: string;
}

export interface Notification {
  type: 'notification';
  title: string;
  message: string;
  level: 'info' | 'success' | 'warning' | 'error';
  data?: any;
  timestamp: string;
}

export interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void;
  onExecutionUpdate?: (update: ExecutionUpdate) => void;
  onTeamExecutionUpdate?: (update: TeamExecutionUpdate) => void;
  onNotification?: (notification: Notification) => void;
  onError?: (error: Event) => void;
  onConnect?: () => void;
  onDisconnect?: () => void;
  autoReconnect?: boolean;
  reconnectInterval?: number;
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const {
    onMessage,
    onExecutionUpdate,
    onTeamExecutionUpdate,
    onNotification,
    onError,
    onConnect,
    onDisconnect,
    autoReconnect = true,
    reconnectInterval = 3000,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connecting' | 'connected' | 'disconnected' | 'error'>('disconnected');
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const subscribedChannelsRef = useRef<Set<string>>(new Set());

  // Cleanup reconnect timeout
  const clearReconnectTimeout = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    const token = localStorage.getItem('access_token');
    if (!token) {
      console.warn('No auth token available, skipping WebSocket connection');
      return;
    }

    try {
      setConnectionStatus('connecting');

      // Determine WebSocket URL based on current location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const envUrl = import.meta.env.VITE_API_URL;
      const host = envUrl ? envUrl.replace(/^https?:\/\//, '') : window.location.host;
      const wsUrl = `${protocol}//${host}/api/v1/ws?token=${token}`;

      console.log('Connecting to WebSocket:', wsUrl);
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        setConnectionStatus('connected');
        clearReconnectTimeout();

        // Send authentication message
        ws.send(JSON.stringify({
          type: 'auth',
          user_id: authStore.user?.id || 'unknown'
        }));

        onConnect?.();
      };

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('WebSocket message received:', message.type, message);

          // Call general message handler
          onMessage?.(message);

          // Route to specific handlers based on message type
          switch (message.type) {
            case 'execution.start':
            case 'execution.progress':
            case 'execution.complete':
            case 'execution.error':
              onExecutionUpdate?.(message as ExecutionUpdate);
              break;

            case 'team_execution.start':
            case 'team_execution.agent_start':
            case 'team_execution.agent_complete':
            case 'team_execution.complete':
              onTeamExecutionUpdate?.(message as TeamExecutionUpdate);
              break;

            case 'notification':
              onNotification?.(message as Notification);
              break;

            case 'connected':
              console.log('WebSocket authentication successful');
              break;

            case 'subscribed':
              console.log('Subscribed to channel:', message.channel);
              break;

            case 'unsubscribed':
              console.log('Unsubscribed from channel:', message.channel);
              break;

            case 'pong':
              // Heartbeat response
              break;

            case 'error':
              console.error('WebSocket error message:', message.message);
              break;

            default:
              console.log('Unknown message type:', message.type);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
        onError?.(error);
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        setConnectionStatus('disconnected');
        subscribedChannelsRef.current.clear();
        wsRef.current = null;
        onDisconnect?.();

        // Auto-reconnect if enabled
        if (autoReconnect && localStorage.getItem('access_token')) {
          console.log(`Reconnecting in ${reconnectInterval}ms...`);
          reconnectTimeoutRef.current = window.setTimeout(() => {
            connect();
          }, reconnectInterval);
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      setConnectionStatus('error');
    }
  }, [authStore.user?.id, autoReconnect, reconnectInterval, onConnect, onDisconnect, onError, onMessage, onExecutionUpdate, onTeamExecutionUpdate, onNotification, clearReconnectTimeout]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    clearReconnectTimeout();
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setConnectionStatus('disconnected');
    subscribedChannelsRef.current.clear();
  }, [clearReconnectTimeout]);

  // Subscribe to a channel
  const subscribe = useCallback((channel: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot subscribe to channel:', channel);
      return;
    }

    if (subscribedChannelsRef.current.has(channel)) {
      console.log('Already subscribed to channel:', channel);
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'subscribe',
      channel
    }));

    subscribedChannelsRef.current.add(channel);
  }, []);

  // Unsubscribe from a channel
  const unsubscribe = useCallback((channel: string) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return;
    }

    wsRef.current.send(JSON.stringify({
      type: 'unsubscribe',
      channel
    }));

    subscribedChannelsRef.current.delete(channel);
  }, []);

  // Send a message
  const sendMessage = useCallback((message: any) => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      console.warn('WebSocket not connected, cannot send message');
      return;
    }

    wsRef.current.send(JSON.stringify(message));
  }, []);

  // Send heartbeat ping
  const sendPing = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'ping' }));
    }
  }, []);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [connect, disconnect]); // Re-connect when user logs in/out

  // Heartbeat interval
  useEffect(() => {
    if (!isConnected) return;

    const interval = setInterval(() => {
      sendPing();
    }, 30000); // Ping every 30 seconds

    return () => clearInterval(interval);
  }, [isConnected, sendPing]);

  return {
    isConnected,
    connectionStatus,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    sendMessage,
  };
}

/**
 * Hook to subscribe to execution updates
 */
export function useExecutionUpdates(
  executionId?: string,
  onUpdate?: (update: ExecutionUpdate) => void
) {
  const { subscribe, unsubscribe, isConnected } = useWebSocket({
    onExecutionUpdate: onUpdate,
  });

  useEffect(() => {
    if (isConnected && executionId) {
      const channel = `execution:${executionId}`;
      subscribe(channel);

      return () => {
        unsubscribe(channel);
      };
    }
  }, [executionId, isConnected, subscribe, unsubscribe]);

  return { isConnected };
}

/**
 * Hook to subscribe to team execution updates
 */
export function useTeamExecutionUpdates(
  executionId?: string,
  onUpdate?: (update: TeamExecutionUpdate) => void
) {
  const { subscribe, unsubscribe, isConnected } = useWebSocket({
    onTeamExecutionUpdate: onUpdate,
  });

  useEffect(() => {
    if (isConnected && executionId) {
      const channel = `team_execution:${executionId}`;
      subscribe(channel);

      return () => {
        unsubscribe(channel);
      };
    }
  }, [executionId, isConnected, subscribe, unsubscribe]);

  return { isConnected };
}
